import React, { useState, useEffect } from 'react';
import { ResponsivePie } from '@nivo/pie';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { formatNumberWithSpaces } from 'utils/index';
import { Ribbon } from 'react-ribbons';
import { useTranslation } from 'react-i18next';

const DistributionSection = ({
  title,
  pieChartData,
  totalSurface,
  idPrefixForPdf,
  collapsible = false,
  defaultExpanded = true,
  currentIndex,
  onIndexChange,
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [internalIndex, setInternalIndex] = useState(0);
  const isControlled = currentIndex !== undefined && onIndexChange !== undefined;
  const activeIndex = isControlled ? currentIndex : internalIndex;
  const setActiveIndex = isControlled ? onIndexChange : setInternalIndex;

  useEffect(() => {
    if (!isControlled) setInternalIndex(0);
  }, [pieChartData]);

  if (!pieChartData?.length) return null;

  const previousYearData = pieChartData[0];
  const forecastYearsData = pieChartData.slice(1);
  const visibleForecastData = forecastYearsData.slice(activeIndex, activeIndex + 2);

  const canGoPrevious = activeIndex > 0;
  const canGoNext = activeIndex + 2 < forecastYearsData.length;

  const buildChartData = (yearPie) => {
    const data = yearPie.data ? [...yearPie.data] : [];
    if (!totalSurface) return data;

    const totalUsed = data.reduce((acc, d) => acc + d.value, 0);
    if (totalUsed < totalSurface) {
      data.push({
        color: '#d1d5db',
        hectares: Number.parseFloat((totalSurface - totalUsed).toFixed(2)),
        id: 'aucune',
        label: t('common.noneFeminine'),
        value: Number.parseFloat((totalSurface - totalUsed).toFixed(2)),
        grossMargin: 0,
        marginPerHectare: 0,
      });
    }
    return data;
  };

  const renderTable = (data) => {
    const cultureRows = data.filter((d) => d.id !== 'aucune');
    const totalGrossMargin = cultureRows.reduce((sum, c) => sum + (c.grossMargin || 0), 0);
    const totalHectares = cultureRows.reduce((sum, c) => sum + (c.hectares || 0), 0);

    return (
      <div className="culture-table-wrapper">
        <table className="culture-table culture-table--compact">
          <thead>
            <tr>
              <th></th>
              <th>{t('common.culture')}</th>
              <th>{t('simulation.result.cultures.table.grossMargin')}</th>
              <th>{t('simulation.result.cultures.table.perHa')}</th>
            </tr>
          </thead>
          <tbody>
            {data.map((culture, idx) => (
              <tr key={idx}>
                <td>
                  <div className="culture-color" style={{ backgroundColor: culture.color }}></div>
                </td>
                <td className="culture-name">{culture.label}</td>
                <td className="culture-value culture-value--highlight">
                  {culture.id === 'aucune'
                    ? '-'
                    : formatNumberWithSpaces((culture.grossMargin || 0).toFixed(0))}
                </td>
                <td className="culture-value culture-value--secondary">
                  {culture.id === 'aucune'
                    ? '-'
                    : formatNumberWithSpaces((culture.marginPerHectare || 0).toFixed(0))}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="culture-table-total">
              <td></td>
              <td>
                <strong>{t('common.total')}</strong>
              </td>
              <td>
                <strong>{formatNumberWithSpaces(totalGrossMargin.toFixed(0))}</strong>
              </td>
              <td>
                <strong>
                  {totalHectares > 0
                    ? formatNumberWithSpaces((totalGrossMargin / totalHectares).toFixed(0))
                    : '-'}
                </strong>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    );
  };

  const renderPieChart = (yearPie, key, readonly = false) => {
    const hasData = yearPie.data?.length > 0;
    const cardClass = `pie-chart-card${readonly ? ' pie-chart-card--readonly' : ' pie-chart-card--active'}`;
    if (!hasData) {
      return (
        <div key={key} className="pie-chart-wrapper">
          <div className={cardClass} style={{ position: 'relative' }}>
            {readonly && (
              <Ribbon
                side="left"
                type="corner"
                size="large"
                withStripes={false}
                backgroundColor="#277134"
                fontFamily="rubik"
                color="#fff"
              >
                <span style={{ fontSize: '0.6rem', whiteSpace: 'nowrap' }}>
                  {t('simulation.result.cultures.previousYear')}
                </span>
              </Ribbon>
            )}
            <div className="pie-chart-header">
              <h3 className="pie-chart-title">
                {yearPie.year}-{yearPie.year + 1}
              </h3>
            </div>
            <div className="pie-chart-empty">{t('simulation.result.cultures.noData')}</div>
          </div>
        </div>
      );
    }

    const chartData = totalSurface ? buildChartData(yearPie) : yearPie.data;
    const referenceSurface = totalSurface || yearPie.data.reduce((sum, d) => sum + d.value, 0);

    return (
      <div key={key} className="pie-chart-wrapper">
        <div className={cardClass} style={{ position: 'relative' }}>
          {readonly && (
            <Ribbon
              side="left"
              type="corner"
              size="large"
              withStripes={false}
              backgroundColor="#277134"
              fontFamily="rubik"
              color="#fff"
            >
              <span style={{ fontSize: '0.6rem', whiteSpace: 'nowrap' }}>
                {t('simulation.result.cultures.previousYear')}
              </span>
            </Ribbon>
          )}

          <div className="pie-chart-header">
            <h3 className="pie-chart-title">
              {yearPie.year}-{yearPie.year + 1}
            </h3>
          </div>
          <div id={`${idPrefixForPdf}-year-${key}`} className="pie-chart-container">
            <ResponsivePie
              data={chartData}
              margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
              innerRadius={0.5}
              padAngle={0.7}
              cornerRadius={3}
              activeOuterRadiusOffset={8}
              colors={{ datum: 'data.color' }}
              borderWidth={1}
              borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
              enableArcLinkLabels={false}
              arcLabelsTextColor="#ffffff"
              arcLabelsSkipAngle={10}
              theme={{
                labels: {
                  text: {
                    fontSize: 11,
                    fontWeight: 600,
                    paintOrder: 'stroke',
                    stroke: '#7170709e',
                    strokeWidth: 2,
                    strokeLinejoin: 'round',
                  },
                },
              }}
              tooltip={({ datum }) => {
                const percentage = ((datum.value / referenceSurface) * 100).toFixed(1);
                return (
                  <div className="tooltip">
                    <strong>{datum.label}</strong>
                    <br />
                    {formatNumberWithSpaces(parseFloat(datum.value).toFixed(2))} ha ({percentage}%)
                  </div>
                );
              }}
            />
          </div>

          {renderTable(chartData)}
        </div>
      </div>
    );
  };

  return (
    <section
      className={`section ${collapsible ? 'section--collapsible' : ''} ${
        collapsible && !isExpanded ? 'section--collapsed' : ''
      }`}
    >
      <div
        className={`section-header ${collapsible ? 'section-header clickable' : ''}`}
        onClick={collapsible ? () => setIsExpanded(!isExpanded) : undefined}
      >
        <div className="section-header-left">
          <h2 className="section-title">{title}</h2>
          {collapsible &&
            (isExpanded ? (
              <ExpandLessIcon className="expand-icon" />
            ) : (
              <ExpandMoreIcon className="expand-icon" />
            ))}
        </div>
      </div>

      {(!collapsible || isExpanded) && (
        <>
          {forecastYearsData.length > 2 && (
            <div className="pie-charts-nav">
              <div className="pie-charts-nav-spacer" />
              <div className="pie-charts-nav-buttons">
                <div className="nav-button--previous">
                  <button
                    className="nav-button"
                    disabled={!canGoPrevious}
                    onClick={() => setActiveIndex(activeIndex - 1)}
                  >
                    {t('common.previous')}
                  </button>
                </div>
                <div className="nav-button--next">
                  <button
                    className="nav-button"
                    disabled={!canGoNext}
                    onClick={() => setActiveIndex(activeIndex + 1)}
                  >
                    {t('common.next')}
                  </button>
                </div>
              </div>
            </div>
          )}
          <div className="pie-charts">
            {previousYearData &&
              renderPieChart(previousYearData, visibleForecastData[0].year - 1, true)}
            {visibleForecastData.map((yearPie) => renderPieChart(yearPie, yearPie.year))}
          </div>
        </>
      )}
    </section>
  );
};

export default DistributionSection;
